/**
 * The look of a crew member marker on every map in the product.
 *
 * One dark disc with the crew member's initials (or their profile
 * photo), a thin ring in their personal colour just inside it, and a
 * lime progress ring on the outside that fills up as their current
 * assignment runs. No assignment → the progress ring stays an empty
 * grey track.
 *
 * Four surfaces draw this marker: the producer live-tracking map, the
 * live-tracking card on the production dashboard, the crew web map and
 * the crew app's native map. They render through different toolkits
 * (imperative DOM for mapbox-gl, react-native-svg for the app), so what
 * is shared here is every *number and colour* the drawing needs. The
 * drawing itself stays per platform:
 *
 *   web    → apps/web/src/lib/crewMarkerElement.ts
 *   mobile → apps/mobile/src/components/CrewMapMarker.tsx
 *
 * Geometry is defined once at CREW_MARKER_BASE_SIZE and scaled, so a
 * 32px marker on the dashboard card is the same design as the 46px one
 * on the full map instead of a second set of hand-tuned numbers.
 */

/** The size the geometry below is authored at; every other size scales from it. */
export const CREW_MARKER_BASE_SIZE = 46;

/**
 * The app's primary accent (--primary in index.css, colors.primary on
 * mobile). The progress ring uses the same lime as the rest of the
 * product rather than a near-miss shade of its own.
 */
const BRAND_LIME = "#d1ff38";

/** Marker size per surface, so the sizes are a design decision in one place. */
export const CREW_MARKER_SIZE = {
  /** Full maps: producer live tracking, the crew map, the crew app. */
  map: CREW_MARKER_BASE_SIZE,
  /** The crew list beside the producer's map. */
  list: 36,
  /** The live-tracking card on the production dashboard. */
  preview: 32,
} as const;

export const CREW_MARKER_COLORS = {
  /** Lime progress arc — the app's primary accent, reserved for "time". */
  progress: BRAND_LIME,
  /** Unfilled part of the progress ring, and the whole ring when free. */
  track: "#38475c",
  /** Disc behind the initials. */
  disc: "#1b2432",
  /** Initials. */
  label: "#ffffff",
  /** Used when a crew member has no personal colour set. */
  fallbackOwn: "#3b82f6",
  // The three below are the web selection/highlight set: they feed CSS
  // box-shadow, which react-native-svg has no equivalent for.
  /** Ring drawn around the selected marker. */
  selectedHalo: "rgba(255, 255, 255, 0.9)",
  /** Glow behind the selected marker. */
  selectedGlow: "rgba(209, 255, 56, 0.45)",
  /** Glow for the "crew member is far from their stage" producer setting. */
  farFromStage: "rgba(249, 115, 22, 0.65)",
} as const;

export type CrewMarkerGeometry = {
  /** Width and height of the circular part. */
  size: number;
  /** Centre point on both axes (also the SVG viewBox centre). */
  center: number;
  /** Radius / stroke width of the outer lime progress ring. */
  progressRadius: number;
  progressWidth: number;
  /** Radius / stroke width of the personal-colour ring just inside it. */
  ownRadius: number;
  ownWidth: number;
  /** Radius of the dark disc holding the initials or photo. */
  discRadius: number;
  /** Profile photo box: fills the disc exactly. */
  photoSize: number;
  photoOffset: number;
  /** Initials type size. */
  fontSize: number;
  /** Pointer under the marker that marks the exact spot. */
  tailWidth: number;
  tailHeight: number;
  /** Total height including the pointer — what the map should reserve. */
  totalHeight: number;
};

/**
 * Geometry for a marker of `size` pixels across, scaled from the 46px
 * reference design. Non-finite or non-positive sizes fall back to the
 * base size so a bad prop can never produce NaN in a style attribute.
 */
export function crewMarkerGeometry(size: number = CREW_MARKER_BASE_SIZE): CrewMarkerGeometry {
  const px = Number.isFinite(size) && size > 0 ? size : CREW_MARKER_BASE_SIZE;
  const k = px / CREW_MARKER_BASE_SIZE;

  const progressWidth = 3 * k;
  const discRadius = 16 * k;
  const tailHeight = 8 * k;

  return {
    size: px,
    center: px / 2,
    progressRadius: 21 * k,
    progressWidth,
    ownRadius: 17.5 * k,
    ownWidth: 2 * k,
    discRadius,
    photoSize: discRadius * 2,
    photoOffset: px / 2 - discRadius,
    fontSize: 13.5 * k,
    tailWidth: 10 * k,
    tailHeight,
    totalHeight: px + tailHeight,
  };
}

export type RingDash = {
  /** Full circumference — the stroke-dasharray to set. */
  circumference: number;
  /** How much of the arc to hide; 0 = full ring, circumference = empty. */
  dashOffset: number;
};

/**
 * Stroke-dash values for a progress arc that fills clockwise from the
 * top. Callers rotate the circle -90° so the arc starts at 12 o'clock.
 *
 * `progress` is clamped, so a shift that ran over (progress > 1) shows a
 * full ring rather than wrapping around a second time.
 */
export function ringDash(radius: number, progress: number): RingDash {
  const safeRadius = Number.isFinite(radius) && radius > 0 ? radius : 0;
  const circumference = 2 * Math.PI * safeRadius;
  const clamped = Number.isFinite(progress) ? Math.min(1, Math.max(0, progress)) : 0;
  return { circumference, dashOffset: circumference * (1 - clamped) };
}

/**
 * How solid a marker looks based on the age of its last GPS fix.
 *
 * Fading is how staleness is communicated now that the numeric GPS
 * readouts (confidence, correction source) are gone from the map: a
 * marker nobody has heard from in a while quietly recedes.
 */
export function markerOpacityForAge(ageSeconds: number | null | undefined): number {
  const age = Number.isFinite(ageSeconds) ? (ageSeconds as number) : 0;
  if (age < 120) return 1;
  if (age < 600) return 0.7;
  if (age < 3600) return 0.45;
  return 0.25;
}

/** Personal colour with the shared fallback applied. */
export function crewOwnColor(color: string | null | undefined): string {
  return color?.trim() || CREW_MARKER_COLORS.fallbackOwn;
}
